<script>
import SvgCapture from "@/components/Svg/SvgCapture.vue";
import SvgRotate from "@/components/Svg/SvgRotate.vue";

export default {
  components: {
    SvgCapture,
    SvgRotate,
  },
  mounted() {
    if (this.value) {
      this.dataImageBase64 = this.value;
    } else {
      this.initializeVideo();
    }
  },
  beforeDestroy() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        track.stop();
      });
    }
  },
  props: {
    value: [String, File, FileList],
    disabled: Boolean,
  },
  data() {
    return {
      dataImageBase64: null,
      stream: null,
      error: null,
    };
  },
  computed: {
    style() {
      const borderColor =
        this.$store.state.config?.colors?.primaryOptionColor || "#51B44F";
      const backgroundColor =
        this.$store.state.config?.colors?.secondaryOptionColor || "#D4F3BB";

      return {
        "--border-color": borderColor,
        "--bg-color": `${backgroundColor}1A`,
      };
    },
  },
  methods: {
    initializeVideo() {
      this.$nextTick(() => {
        const video = this.$refs?.videoElement;
        const constraints = {
          video: { facingMode: "user" },
        };

        navigator.mediaDevices
          .getUserMedia(constraints)
          .then((stream) => {
            this.stream = stream;
            video.srcObject = this.stream;
          })
          .catch((err) => {
            console.error(err);
            this.error = "Permission error. Please allow camera access.";
          });
      });
    },
    onCapture() {
      const video = this.$refs?.videoElement;
      const canvas = this.$refs?.canvasElement;

      if (video) {
        const context = canvas.getContext("2d");

        context.drawImage(video, 0, 0, 640, 480);
        this.dataImageBase64 = canvas.toDataURL("image/png");
        this.$emit("input", this.dataImageBase64);

        const base64String = this.dataImageBase64.split(",")[1];
        this.$emit("change", base64String);

        if (this.stream) {
          this.stream.getTracks().forEach((track) => {
            track.stop();
          });
        }
      }
    },
    reset() {
      this.dataImageBase64 = null;
      this.$emit("input", null);
      this.initializeVideo();
    },
  },
};
</script>

<template>
  <div :style="style">
    <div
      class="relative rounded-xl border border-color bg-color min-h-210px select-none"
      :class="{ 'cursor-not-allowed opacity-50 pointer-events-none': disabled }"
      role="application"
      aria-label="Camera capture interface"
    >
      <div class="flex flex-col rounded-xl px-10 pt-10 pb-6 space-y-6">
        <div v-if="!dataImageBase64" class="flex flex-col space-y-1">
          <span class="text-sm font-bold" id="camera-label">Camera Preview</span>
          <video
            class="bg-black"
            ref="videoElement"
            width="640"
            height="480"
            autoplay
            aria-labelledby="camera-label"
            role="img"
          ></video>
          <span
            v-if="error"
            class="text-red-400 text-sm mx-auto"
            role="alert"
            aria-live="assertive"
          >
            {{ error }}
          </span>
        </div>
        <img
          v-else
          :src="dataImageBase64"
          width="640"
          height="480"
          alt="Captured photo"
          role="img"
        />
        <canvas
          ref="canvasElement"
          width="640"
          height="480"
          style="display: none"
          aria-hidden="true"
        ></canvas>
        <div class="flex">
          <button
            v-if="!dataImageBase64"
            type="button"
            class="appearance-none mx-auto"
            :class="{ 'opacity-50 pointer-events-none': error }"
            :disabled="error"
            :aria-disabled="error"
            aria-label="Capture Photo"
            @click="onCapture"
          >
            <svg-capture aria-hidden="true" role="img"></svg-capture>
          </button>
          <button
            v-else
            type="button"
            class="btn-reset appearance-none bg-gray-200 font-semibold inline-flex items-center px-4 rounded-full space-x-2 mx-auto"
            @click="reset"
            aria-label="Retake Photo"
          >
            <svg-rotate aria-hidden="true" role="img"></svg-rotate>
            <span>Retake</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bg-color {
  background-color: inherit;
}
.min-h-210px {
  min-height: 210px;
}
.border-color {
  border-color: var(--border-color);
}
.bg-color {
  background-color: var(--bg-color);
}
.button {
  background-color: #dadbdb;
  height: 45px;
}
.button:hover {
  background-color: #dadbdbcc;
}
.button-remove {
  background-color: #fb00001a;
  color: #ef1212;
  height: 36px;
}
.h-314px {
  height: 314px;
  width: 100%;
}
.btn-reset {
  height: 48px;
  background-color: #bcbcbc;
  color: #757575;
}
</style>
